<template>
    <div
        v-if="loading || error || notAuthenticated"
        class="h-full flex flex-col items-center justify-center align-middle">
        <CirqllLogo class="h-28 w-max lt-md:w-52" />
        <div
            v-if="error"
            class="text-center text-danger">
            <h2 class="mb-0">{{ error }}</h2>
            <h4>{{ t('basic.try_again') }},</h4>
            <h4>{{ t('basic.or_call_help') }} <a href="tel:0850163356">085 016 3356</a></h4>
            <div class="mt-5 flex flex-col justify-center gap-3">
                <button
                    class="btn"
                    @click="reload">
                    {{ t('basic.try_again') }}
                </button>
                <button
                    class="btn bg-danger"
                    @click="gotoLogin">
                    {{ t('basic.logout') }}
                </button>
            </div>
        </div>
        <button
            v-else-if="notAuthenticated && !isCompletingLogin"
            class="btn mt-5"
            @click="gotoLogin">
            {{ t('basic.login') }}
        </button>
        <CirqllLoading v-else />
    </div>
    <RouterView v-else />
    <WorkFlow />
    <CirqllSnowfall v-if="decorations.includes('snow')" />
    <CirqllPepernoten />
</template>

<script setup lang="ts">
    import * as sdk from '@cirqll/sdk';
    import { useQueryClient } from '@tanstack/vue-query';
    import status from 'http-status';
    import { preload } from './utils';
    import { setOptions } from '@googlemaps/js-api-loader';
    import { GOOGLE_API_KEY } from '~/constants';
    import type { RouteNamedMap } from 'vue-router/auto-routes';
    import { fetchCurrentUser, isCompletingLogin, redirectToActivateAccount, redirectToLogin } from '~/utils/auth';
    import { ALWAYS_ALLOWED_ROUTES } from '~/modules/auth';
    import { useThemeDecorations } from '@cirqll/storybook/theme/vue';

    sdk.setQueryClient(useQueryClient());

    const { t } = useI18n();
    const loading = ref(true);
    const decorations = useThemeDecorations();

    // https://unhead.unjs.io/
    // You can use this to manipulate the document head in any component
    useHead({
        title: t('app_name'),
        meta: [{ name: 'description', content: t('app_slogan') }],
    });

    setOptions({ key: GOOGLE_API_KEY });

    const error = ref<string>();
    const notAuthenticated = ref(false);

    onMounted(async () => {
        if (ALWAYS_ALLOWED_ROUTES.includes(window.location.pathname as keyof RouteNamedMap)) {
            loading.value = false;
            return;
        }

        try {
            const me = await fetchCurrentUser();

            // TODO: remove 30 days after the email-verification-at-authorize release (CIRQLL-2194)
            if (me.email_verified_at === null) {
                void redirectToActivateAccount();
                return;
            }

            preload(me);
            loading.value = false;
        } catch (e) {
            if ((e as any).response?.status === status.UNAUTHORIZED) {
                setTimeout(() => {
                    notAuthenticated.value = true;
                    loading.value = false;
                }, 300);
            } else if (e && typeof e === 'object' && 'message' in e) {
                error.value = e.message || (e as any).toString();
                loading.value = false;
            }
        }
    });

    function reload() {
        window.location.reload();
    }

    function gotoLogin() {
        error.value = undefined;
        void redirectToLogin();
    }
</script>
